import { paths } from '@/configs/paths'
import { ROLES } from '@/constants/common'
import { convertToRouteObject } from '@/lib/router-utils'
import { QueryClient } from '@tanstack/react-query'
import { type RouteObject } from 'react-router-dom'
import { ProtectedRoute } from '@/middleware/ProtectedRoute'
import { MainLayout } from '@/components/layouts/MainLayout'
import ErrorFallback from '@/components/layouts/base/ErrorFallback'
import UserLanding from '@/middleware/UserLanding'

export const createProtectedRoutes = (queryClient: QueryClient): RouteObject => {
  const convert = convertToRouteObject(queryClient)

  return {
    path: paths.root.path,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    ErrorBoundary: ErrorFallback,
    children: [
      {
        index: true,
        element: <UserLanding />
      },
      /* System */
      {
        path: paths.distributorManage.path,
        lazy: () => import('@/pages/distributorManage').then(convert),
        handle: { allowedRoles: [ROLES.SYSTEM] }
      },
      {
        path: paths.distributorManageCreate.path,
        lazy: () => import('@/pages/distributorManage/Create').then(convert),
        handle: { allowedRoles: [ROLES.SYSTEM] }
      },
      {
        path: paths.distributorManageDetail.path,
        lazy: () => import('@/pages/distributorManage/Detail').then(convert),
        handle: { allowedRoles: [ROLES.SYSTEM] }
      },
      {
        path: paths.distributorManageEdit.path,
        lazy: () => import('@/pages/distributorManage/Edit').then(convert),
        handle: { allowedRoles: [ROLES.SYSTEM] }
      },
      /** Stock */
      {
        path: paths.stockMaster.path,
        lazy: () => import('@/pages/stockMaster').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },

      /** Suppliers */
      {
        path: paths.supplierMaster.path,
        lazy: () => import('@/pages/suppliersMaster').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.supplierMasterCreate.path,
        lazy: () => import('@/pages/suppliersMaster/Create').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.supplierMasterDetail.path,
        lazy: () => import('@/pages/suppliersMaster/Detail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.supplierMasterEdit.path,
        lazy: () => import('@/pages/suppliersMaster/Edit').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },

      /** Customers */
      {
        path: paths.customerMaster.path,
        lazy: () => import('@/pages/customersMaster').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.customerMasterCreate.path,
        lazy: () => import('@/pages/customersMaster/Create').then(convert),
        handle: { allowedRoles: [ROLES.SYSTEM] }
      },
      {
        path: paths.customerMasterDetail.path,
        lazy: () => import('@/pages/customersMaster/Detail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.customerMasterEdit.path,
        lazy: () => import('@/pages/customersMaster/Edit').then(convert),
        handle: { allowedRoles: [ROLES.SYSTEM] }
      },
      {
        path: paths.customerMasterProducts.path,
        lazy: () => import('@/pages/customersMaster/Products').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.customerMasterEditProducts.path,
        lazy: () => import('@/pages/customersMaster/EditProduct').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SYSTEM] }
      },
      // /** Stocks */
      {
        path: paths.inStock.path,
        lazy: () => import('@/pages/stocks/InStockPage').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.inTransit.path,
        lazy: () => import('@/pages/stocks/InTransitPage').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      /** Products Master */
      {
        path: paths.productMaster.path,
        lazy: () => import('@/pages/productsMaster').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.productMasterCreate.path,
        lazy: () => import('@/pages/productsMaster/Create').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.productMasterDetail.path,
        lazy: () => import('@/pages/productsMaster/Detail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.productMasterEdit.path,
        lazy: () => import('@/pages/productsMaster/Edit').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },

      /** Sales */
      {
        path: paths.salesBuy.path,
        lazy: () => import('@/pages/sales/BuyPage').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },
      {
        path: paths.salesReturn.path,
        lazy: () => import('@/pages/sales/ReturnPage').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.SYSTEM] }
      },

      // /** Orders (Supplier) */
      {
        path: paths.ordersSalesBySupplier.path,
        lazy: () => import('@/pages/orders/SalesBySupplier').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersManagementList.path,
        lazy: () => import('@/pages/orders/Management').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersManagementCreate.path,
        lazy: () => import('@/pages/orders/CreateManagement').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersManagementDetail.path,
        lazy: () => import('@/pages/orders/DetailManagement').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersManagementEdit.path,
        lazy: () => import('@/pages/orders/EditManagement').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersMissingList.path,
        lazy: () => import('@/pages/orders/MissingProduct').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersMissingCreate.path,
        lazy: () => import('@/pages/orders/MissingProductCreate').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersMissingEdit.path,
        lazy: () => import('@/pages/orders/MissingProductEdit').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersMissingDetail.path,
        lazy: () => import('@/pages/orders/MissingProductDetail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ordersManagementEmail.path,
        lazy: () => import('@/pages/orders/SendEmail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.inboundCreate.path,
        lazy: () => import('@/pages/inbound/Create').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.inboundEdit.path,
        lazy: () => import('@/pages/inbound/Edit').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.inboundDetail.path,
        lazy: () => import('@/pages/inbound/Detail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.inboundList.path,
        lazy: () => import('@/pages/inbound').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.outboundList.path,
        lazy: () => import('@/pages/outbound').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.outboundCreate.path,
        lazy: () => import('@/pages/outbound/Create').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.outboundDetail.path,
        lazy: () => import('@/pages/outbound/Detail').then(convert),
        handle: {
          allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.DELIVERY_STAFF]
        }
      },
      {
        path: paths.outboundEdit.path,
        lazy: () => import('@/pages/outbound/Edit').then(convert),
        handle: {
          allowedRoles: [ROLES.DISTRIBUTOR_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.DELIVERY_STAFF]
        }
      },
      {
        path: paths.ngGoodsHistoryList.path,
        lazy: () => import('@/pages/defectHistory').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ngGoodsHistoryCreate.path,
        lazy: () => import('@/pages/defectHistory/Create').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ngGoodsHistoryEdit.path,
        lazy: () => import('@/pages/defectHistory/Edit').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      {
        path: paths.ngGoodsHistoryDetail.path,
        lazy: () => import('@/pages/defectHistory/Detail').then(convert),
        handle: { allowedRoles: [ROLES.DISTRIBUTOR_ADMIN] }
      },
      /* Customer Staff */
      {
        path: paths.stockersList.path,
        lazy: () => import('@/pages/stockers').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.productsSelect.path,
        lazy: () => import('@/pages/productSelect').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.productsDetailMobile.path,
        lazy: () => import('@/pages/productSelect/Detail').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.cartConfirm.path,
        lazy: () => import('@/pages/cart/Confirm').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.cartSuccess.path,
        lazy: () => import('@/pages/cart/Success').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.cartList.path,
        lazy: () => import('@/pages/cart').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.historyList.path,
        lazy: () => import('@/pages/history').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.settings.path,
        lazy: () => import('@/pages/settings').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_STAFF, ROLES.CUSTOMER_ADMIN] }
      },

      /* Customer Admin */
      {
        path: paths.selectMode.path,
        lazy: () => import('@/pages/selectMode').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.deliveries.path,
        lazy: () => import('@/pages/deliveries').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.purchaseBuy.path,
        lazy: () => import('@/pages/purchase/BuyPage').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.purchaseReturn.path,
        lazy: () => import('@/pages/purchase/ReturnPage').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.userMaster.path,
        lazy: () => import('@/pages/users').then(convert),
        handle: { allowedRoles: [ROLES.CUSTOMER_ADMIN] }
      },
      {
        path: paths.deliveryStaff.path,
        lazy: () => import('@/pages/deliveryStaff').then(convert),
        handle: { allowedRoles: [ROLES.DELIVERY_STAFF] }
      }
    ]
  }
}
